import { FlamingoAuthService } from '@flamingo/service/flamingo-auth.service';
import { FuseNavigation } from '@fuse/types';
import { MainService } from '../children/main/main.service';
import { PartnerPermission, UserType } from '../core/model/user.model';

export const getNavigation = (authService: FlamingoAuthService, mainService: MainService): FuseNavigation[] => {
  switch (authService.user?.type) {
    case UserType.seller:
      return getSellerNavigation(authService);

    case UserType.buyer:
      return getBuyerNavigation(authService);

    case UserType.partner:
    case UserType.partnerBranchMaster:
    case UserType.partnerAgent:
      return getPartnerNavigation(authService, mainService, authService.user?.type);

    default:
      return [];
  }
};

function getSellerNavigation(authService: FlamingoAuthService): FuseNavigation[] {
  return [
    {
      id: 'account',
      title: 'Account',
      translate: 'NAV.ACCOUNT',
      type: 'group',
      children: [
        {
          id: 'profile',
          title: 'Profilo',
          translate: 'NAV.PROFILE',
          type: 'item',
          icon: 'person',
          url: '/profile',
        },
        {
          id: 'document',
          title: 'Documenti',
          translate: 'NAV.SELLER.DOCUMENT',
          type: 'item',
          icon: 'description',
          url: '/document',
        },
        {
          id: 'document_aml',
          title: 'Documenti AML',
          translate: 'NAV.SELLER.DOCUMENT_AML',
          type: 'item',
          icon: 'description',
          url: '/document-aml',
        },
      ],
    },
    {
      id: 'seller',
      title: 'Seller',
      type: 'group',
      children: [
        {
          id: 'invoice',
          title: 'Fatture',
          translate: 'NAV.SELLER.INVOICE',
          type: 'collapsable',
          icon: 'assignment',
          children: [
            {
              id: 'invoice_add',
              title: 'Add Invoice',
              translate: 'NAV.SELLER.INVOICE_ADD',
              type: 'item',
              url: '/invoice',
            },
            {
              id: 'invoice_evaluation',
              title: 'Invoice Evaluation',
              translate: 'NAV.SELLER.INVOICE_EVALUATION',
              type: 'collapsable',
              children: [
                {
                  id: 'invoice_evaluation-all',
                  title: 'All',
                  translate: 'NAV.SELLER.INVOICE_EVALUATION_ALL',
                  type: 'item',
                  url: '/auction/all',
                },
                {
                  id: 'invoice_evaluation-pending_invoice_validation',
                  title: 'Pending Invoice Validation',
                  translate: 'NAV.SELLER.INVOICE_EVALUATION_PENDING_INVOICE_VALIDATION',
                  type: 'item',
                  url: '/auction/sent/pending-invoice-validation',
                },
                {
                  id: 'invoice_evaluation-pending_debtor_validation',
                  title: 'Pending Debtor Validation',
                  translate: 'NAV.SELLER.INVOICE_EVALUATION_PENDING_DEBTOR_VALIDATION',
                  type: 'item',
                  url: '/auction/sent/pending-debtor-validation',
                },
                {
                  id: 'invoice_evaluation-ready',
                  title: 'Ready',
                  translate: 'NAV.SELLER.INVOICE_EVALUATION_READY',
                  type: 'item',
                  url: '/auction/sent/ready',
                },
                {
                  id: 'invoice_evaluation-invalid',
                  title: 'Invalid',
                  translate: 'NAV.SELLER.INVOICE_EVALUATION_INVALID',
                  type: 'item',
                  url: '/auction/sent/invalid',
                },
              ],
            },
            {
              id: 'in-progress',
              title: 'In progress',
              translate: 'NAV.SELLER.AUCTION_IN_PROGRESS',
              type: 'item',
              url: '/auction/in-progress',
            },
            {
              id: 'sold',
              title: 'Sold',
              translate: 'NAV.SELLER.INVOICE_SOLD',
              type: 'collapsable',
              children: [
                {
                  id: 'sold-active',
                  title: 'Sold Active',
                  translate: 'NAV.SELLER.INVOICE_SOLD_ACTIVE',
                  type: 'item',
                  url: '/auction/sold-active',
                },
                {
                  id: 'sold-archived',
                  title: 'Sold Archived',
                  translate: 'NAV.SELLER.INVOICE_SOLD_ARCHIVED',
                  type: 'item',
                  url: '/auction/sold-archived',
                },
              ],
            },
            {
              id: 'expired',
              title: 'Expired',
              translate: 'NAV.SELLER.AUCTION_EXPIRED',
              type: 'item',
              url: '/auction/expired',
            },
          ],
        },
        {
          id: 'client',
          title: 'Clienti',
          translate: 'NAV.SELLER.CLIENT',
          type: 'item',
          icon: 'group',
          url: '/client',
        },
      ],
    },
  ];
}

function getBuyerNavigation(authService: FlamingoAuthService): FuseNavigation[] {
  return [
    {
      id: 'account',
      title: 'Account',
      translate: 'NAV.ACCOUNT',
      type: 'group',
      children: [
        {
          id: 'profile',
          title: 'Profilo',
          translate: 'NAV.PROFILE',
          type: 'item',
          icon: 'person',
          url: '/profile',
        },
      ],
    },
    {
      id: 'buyer',
      title: 'Buyer',
      type: 'group',
      children: [
        // TODO dashboard
        /*    {
              id: 'home',
              title: 'Home',
              type: 'item',
              icon: 'home',
              url: '/dashboard'
            },*/
        {
          id: 'auction-progress',
          title: 'Auction in progress',
          translate: 'NAV.BUYER.AUCTIONS_PROGRESS',
          type: 'item',
          icon: 'watch_later',
          url: '/auction-progress',
        },
        {
          id: 'auction-awarded',
          title: 'Auction awarded',
          translate: 'NAV.BUYER.AUCTIONS_AWARDED',
          type: 'collapsable',
          icon: 'check',
          // url: '/auction-awarded',
          children: [
            {
              id: 'auction-awarded-all',
              title: 'All',
              translate: 'NAV.BUYER.AUCTIONS_AWARDED_ALL',
              type: 'item',
              url: '/auction-awarded',
            },
            {
              id: 'auction-awarded-pending-downpayment',
              title: 'Pending downpayment',
              translate: 'NAV.BUYER.AUCTIONS_AWARDED_PENDING_DOWNPAYMENT',
              type: 'item',
              url: '/auction-awarded-pending-downpayment',
            },
            {
              id: 'auction-awarded-pending-settlement',
              title: 'Pending settlement',
              translate: 'NAV.BUYER.AUCTIONS_AWARDED_PENDING_SETTLEMENT',
              type: 'item',
              url: '/auction-awarded-pending-settlement',
            },
          ],
        },
      ],
    },
  ];
}

function getPartnerNavigation(authService: FlamingoAuthService, mainService: MainService, userType: UserType): FuseNavigation[] {
  const navigation: FuseNavigation[] = [
    {
      id: 'account',
      title: 'Account',
      userType,
      translate: 'NAV.ACCOUNT',
      type: 'group',
      children: [
        {
          id: 'profile',
          title: 'Profilo',
          translate: 'NAV.PROFILE',
          type: 'item',
          icon: 'person',
          url: '/profile',
        },
        {
          id: 'users',
          title: 'Gestione utenti',
          translate: 'NAV.USERS_MANAGEMENT',
          type: 'item',
          icon: 'people',
          url: '/user',
          hidden: authService.cannot([UserType.partner]),
        },
      ],
    },
    {
      id: 'partner',
      title: 'Partner',
      type: 'group',
      children: [
        {
          id: 'auction',
          title: 'Fatture',
          translate: 'NAV.PARTNER.AUCTIONS',
          type: 'collapsable',
          icon: 'gavel',
          children: [
            {
              id: 'report',
              title: 'Aste cedute',
              translate: 'NAV.PARTNER.AUCTION_REPORT',
              type: 'item',
              url: '/auction/report',
            },
            {
              id: 'expired',
              title: 'Aste non cedute',
              translate: 'NAV.PARTNER.AUCTION_EXPIRED',
              type: 'item',
              url: '/auction/expired',
            },
          ],
        },
        {
          id: 'companies',
          title: 'Aziende',
          translate: 'NAV.PARTNER.COMPANIES',
          type: 'collapsable',
          icon: 'business',
          children: [
            {
              id: 'seller',
              title: 'Seller',
              type: 'item',
              url: '/company/seller',
            },
            {
              id: 'debtor',
              title: 'Debtor',
              type: 'item',
              url: '/company/debtor',
            },
          ],
        },
      ],
    },
  ];

  const navigationForbiddenSections = mainService.user.partnerPermissions.includes(PartnerPermission.All)
    ? []
    : navigationPartnerPermission
      .filter(p => !mainService.user.partnerPermissions.includes(p.permission))
      .map(p => p.navId);

  navigation.forEach((n, i, array) => {
    n.children = n.children.filter(navItem => !navigationForbiddenSections.includes(navItem.id));

    // Remove empty sections
    if (n.children.length === 0) {
      array.splice(i, 1);
    }
  });

  return navigation;
}

export const navigationPartnerPermission: { permission: PartnerPermission; navId: string }[] = [
  { permission: PartnerPermission.Accounts, navId: 'users' },
  { permission: PartnerPermission.Companies, navId: 'companies' },
  { permission: PartnerPermission.Invoices, navId: 'auction' },
];
